// Stage 73.20dm (Phase 3) — TDS Validation Report page.
//
// Route: /reports/tds-validation
// RBAC: tenant_admin, super_admin, hr_admin, payroll_admin, payroll_processor
//
// Shows per-employee TDS status:
//   - Employee ID / Name / Regime
//   - Processed & projection months, projection source
//   - Annual projected taxable, annual tax payable
//   - TDS deducted till prev month / current month / till current month
//   - Closing balance TDS
//   - Tax sheet generated / released / employee portal visible / download count
//   - Error remarks, stale flag & reason
//
// Filters: financial_year, run_id, regime, department, location, payroll_group.
// Actions: refresh, regenerate for a run, download CSV, bulk PDF zip.

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Download, RefreshCw, Filter, FileSpreadsheet, FileArchive, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const _currentFy = () => {
  const d = new Date();
  const s = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${s}-${String((s + 1) % 100).padStart(2, '0')}`;
};

const inr = (n) => Number(n || 0).toLocaleString('en-IN');

const StatusChip = ({ value }) => {
  if (value === 'Yes') return <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full text-xs"><CheckCircle2 className="w-3 h-3" />{value}</span>;
  if (value === 'No') return <span className="inline-flex items-center gap-1 text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full text-xs"><XCircle className="w-3 h-3" />{value}</span>;
  if ((value || '').startsWith('Stale')) return <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full text-xs"><AlertTriangle className="w-3 h-3" />Stale</span>;
  return <span className="text-xs text-slate-500">{value}</span>;
};

export default function TDSValidationReport() {
  const [fy, setFy] = useState(_currentFy());
  const [runId, setRunId] = useState('');
  const [regime, setRegime] = useState('');
  const [department, setDepartment] = useState('');
  const [location, setLocation] = useState('');
  const [payrollGroup, setPayrollGroup] = useState('');
  const [runs, setRuns] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, generated: 0, released: 0, stale: 0, errors: 0 });
  const [loading, setLoading] = useState(false);
  const [fyOptions, setFyOptions] = useState([]);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/tax-slab-master/meta/financial-years`, { withCredentials: true })
      .then(r => setFyOptions(r.data?.financial_years || [_currentFy()]))
      .catch(() => setFyOptions([_currentFy()]));
    axios.get(`${API_URL}/payroll/runs?limit=50`, { withCredentials: true })
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : (r.data?.runs || []);
        setRuns(data);
      })
      .catch(() => {});
  }, []);

  // Stage 73.20dm (Phase 3) — Auto-derive FY when a run is selected so
  // the row-count matches the run's actual financial year (avoids the
  // "No snapshot" remark that appears when FY doesn't align).
  useEffect(() => {
    if (!runId) return;
    const run = runs.find(r => (r.id || r._id) === runId);
    if (!run) return;
    const y = run.statutory_year;
    const m = run.statutory_month;
    if (!y || !m) return;
    const fyStart = m >= 4 ? y : y - 1;
    const runFy = `${fyStart}-${String((fyStart + 1) % 100).padStart(2, '0')}`;
    if (runFy && runFy !== fy) setFy(runFy);
  }, [runId, runs, fy]);


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fy });
      if (runId) params.set('run_id', runId);
      if (regime) params.set('regime', regime);
      if (department) params.set('department', department);
      if (location) params.set('location', location);
      if (payrollGroup) params.set('payroll_group', payrollGroup);
      const r = await axios.get(`${API_URL}/reports/tds-validation?${params}`, { withCredentials: true });
      setRows(r.data?.rows || []);
      setSummary({
        total: r.data?.total || 0,
        generated: r.data?.generated || 0,
        released: r.data?.released || 0,
        stale: r.data?.stale || 0,
        errors: r.data?.errors || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load validation report');
    } finally {
      setLoading(false);
    }
  }, [department, fy, location, payrollGroup, regime, runId]);

  useEffect(() => { load(); }, [fy, runId, regime, department, location, payrollGroup, load]);

  const downloadCsv = () => {
    const params = new URLSearchParams({ fy });
    if (runId) params.set('run_id', runId);
    if (regime) params.set('regime', regime);
    if (department) params.set('department', department);
    if (location) params.set('location', location);
    if (payrollGroup) params.set('payroll_group', payrollGroup);
    window.open(`${API_URL}/reports/tds-validation.csv?${params}`, '_blank');
  };

  const bulkPdfZip = () => {
    if (!runId) { toast.error('Select a Payroll Run first for bulk PDF download.'); return; }
    const params = new URLSearchParams({ fy });
    if (regime) params.set('regime', regime);
    if (department) params.set('department', department);
    if (location) params.set('location', location);
    if (payrollGroup) params.set('payroll_group', payrollGroup);
    params.set('released_only', 'false');
    window.open(`${API_URL}/payroll/runs/${runId}/tax-sheets.zip?${params}`, '_blank');
  };

  const regenerate = async () => {
    if (!runId) { toast.error('Select a Payroll Run first.'); return; }
    setRegenerating(true);
    try {
      const r = await axios.post(`${API_URL}/tax-sheet/auto/regenerate/${runId}`, {}, { withCredentials: true });
      if (r.data?.ok) {
        toast.success(`Regenerated ${r.data.generated} tax sheets · FY ${r.data.fy}`);
        await load();
      } else {
        toast.error(r.data?.reason || 'Regeneration failed.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regeneration failed.');
    } finally {
      setRegenerating(false);
    }
  };

  const summaryCards = useMemo(() => [
    { label: 'Total Employees', value: summary.total, color: 'text-slate-800', bg: 'bg-slate-50' },
    { label: 'Generated', value: summary.generated, color: 'text-emerald-700', bg: 'bg-emerald-50' },
    { label: 'Released', value: summary.released, color: 'text-blue-700', bg: 'bg-blue-50' },
    { label: 'Stale (recalc needed)', value: summary.stale, color: 'text-amber-700', bg: 'bg-amber-50' },
    { label: 'Errors', value: summary.errors, color: 'text-red-700', bg: 'bg-red-50' },
  ], [summary]);

  return (
    <MainLayout>
      <div data-testid="tds-validation-report" className="space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-[#0052FF]" /> TDS Validation Report
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Per-employee TDS projection + release + download status. Filter by FY,
              Run, Regime, Department, Location or Payroll Group.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="regenerate-btn"
              onClick={regenerate}
              disabled={!runId || regenerating}
              className="flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-700 px-3 py-2 rounded-md text-sm font-medium"
              title="Regenerate snapshots for the selected Payroll Run"
            >
              <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} /> Regenerate
            </button>
            <button
              data-testid="download-csv-btn"
              onClick={downloadCsv}
              className="flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded-md text-sm font-medium"
            >
              <Download className="w-4 h-4" /> Download CSV
            </button>
            <button
              data-testid="bulk-pdf-zip-btn"
              onClick={bulkPdfZip}
              disabled={!runId}
              className="flex items-center gap-1.5 bg-[#0052FF] hover:bg-[#0040CC] disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-3 py-2 rounded-md text-sm font-medium"
              title={runId ? 'Bulk Tax Sheet PDFs (ZIP) filtered by the current filters' : 'Select a Payroll Run first'}
            >
              <FileArchive className="w-4 h-4" /> Bulk PDF ZIP
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {summaryCards.map(c => (
            <div key={c.label} data-testid={`summary-${c.label.replace(/\s+/g, '-').toLowerCase()}`} className={`${c.bg} rounded-lg border border-slate-100 p-3`}>
              <p className="text-xs text-slate-500">{c.label}</p>
              <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-700">Filters</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <label className="text-xs text-slate-500">Financial Year</label>
              <select data-testid="filter-fy" value={fy} onChange={e => setFy(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
                {fyOptions.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-500">Payroll Run</label>
              <select data-testid="filter-run" value={runId} onChange={e => setRunId(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
                <option value="">— Any —</option>
                {runs.map(r => (
                  <option key={r.id || r._id} value={r.id || r._id}>{`${r.period_name || r.description || r.run_number || (r.id || r._id).slice(-6)} · ${r.status}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Regime</label>
              <select data-testid="filter-regime" value={regime} onChange={e => setRegime(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
                <option value="">Both</option><option value="new">New</option><option value="old">Old</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Department</label>
              <input data-testid="filter-department" type="text" value={department} onChange={e => setDepartment(e.target.value)} placeholder="e.g. Engineering" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Location</label>
              <input data-testid="filter-location" type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Bangalore" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Payroll Group</label>
              <input data-testid="filter-payroll-group" type="text" value={payrollGroup} onChange={e => setPayrollGroup(e.target.value)} placeholder="e.g. STAFF" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-slate-700">Emp ID</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-700">Name</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-700">Dept</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-700">Regime</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-700">Months P/R</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-700">Annual Taxable</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-700">Annual Tax</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-700">TDS Prev</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-700">TDS Curr</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-700">Closing Bal</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-700">Gen</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-700">Rel</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-700">ESS Visible</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-700">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={14} className="text-center py-8 text-slate-400">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={14} className="text-center py-8 text-slate-400">
                  No employees match the current filters. Try selecting a different FY or Payroll Run.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.employee_object_id} data-testid={`row-${r.employee_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800">{r.employee_id}</td>
                  <td className="px-3 py-2 text-slate-700">{r.name}</td>
                  <td className="px-3 py-2 text-slate-600">{r.department || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{r.tax_regime}</td>
                  <td className="px-3 py-2 text-center text-slate-600">{r.processed_months}/{r.projection_months}</td>
                  <td className="px-3 py-2 text-right text-slate-700">₹{inr(r.annual_projected_taxable)}</td>
                  <td className="px-3 py-2 text-right font-medium text-slate-800">₹{inr(r.annual_tax_payable)}</td>
                  <td className="px-3 py-2 text-right text-slate-600">₹{inr(r.tds_till_previous_month)}</td>
                  <td className="px-3 py-2 text-right text-slate-600">₹{inr(r.current_month_tds)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${(r.closing_balance_tds || 0) > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                    ₹{inr(Math.abs(r.closing_balance_tds || 0))}
                  </td>
                  <td className="px-3 py-2 text-center"><StatusChip value={r.tax_sheet_generated} /></td>
                  <td className="px-3 py-2 text-center"><StatusChip value={r.tax_sheet_released} /></td>
                  <td className="px-3 py-2 text-center"><StatusChip value={r.employee_portal_visible} /></td>
                  <td className="px-3 py-2 text-slate-500 max-w-xs truncate" title={r.error_remarks}>{r.error_remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </MainLayout>
  );
}
